import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { SearchIcon } from '@heroicons/react/outline';
import { giftItemInfoListDataAtom } from '@shared/api/giftItemInfo/giftItemInfo.atom';
import { useGiftItemInfoListHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import {
  GiftItemInfoData,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import {
  GiftItemPreviewModal,
  giftDiscountRate,
  giftImageThumbUrl,
} from '@shared/components/GiftItemPreviewModal';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { formatPrice } from '@shared/utils/formatPrice';
import { useAsyncFn } from 'react-use';

/**
 * 판매상품관리 (답례품 배송관리자 / Admin) — 조회 전용.
 *
 * 본인 브랜드(공급사)의 상품이 지금 판매 중인지·판매종료인지와
 * 정가/판매가/공급가를 한 화면에서 확인하고, 상품을 누르면
 * 전화 응대 직원이 보는 것과 같은 상세 모달(GiftItemPreviewModal)을 연다.
 *
 * 데이터: `{role}/gift-item-info` (Admin=전 브랜드, GiftBrandSeller=본인 브랜드만
 * — 백엔드 owningFilter가 강제, 읽기 전용 컨트롤러라 수정 불가).
 * 가격은 원상품 기준값이고, 실제 상점 판매가는 상조별 GiftItem에서 덮어쓸 수 있어
 * 상조별 실판매 현황은 아래 선배선(`{role}/gift-item`)이 열리면 함께 보여준다.
 */

type StatusFilter = 'all' | 'onSale' | 'ended';
type TypeFilter = 'all' | 'coupon' | 'delivery';

//* 상조별 실제 판매 현황 (선배선) — 필요한 필드만
interface GiftItemLite {
  _id: string;
  giftItemInfo?: string | { _id: string };
  priceRetail?: number;
}

interface SaleStat {
  /** 이 상품을 판매 중인 상조 수 */
  count: number;
  minPrice: number;
  maxPrice: number;
}

const infoIdOf = (item: GiftItemLite): string | undefined => {
  const info = item.giftItemInfo;
  if (!info) return undefined;
  return typeof info === 'object' ? info._id : info;
};

/**
 * 상조별 상품(GiftItem) 집계. Admin은 지금도 동작하고,
 * 배송관리자는 `gift-brand-seller/gift-item` 라우트가 열린 뒤부터 동작한다
 * (미배포 서버는 404 → 이 정보만 숨기고 나머지는 정상 표시).
 */
const fetchSaleStats = async (
  role: LowerRole.Admin | LowerRole.GiftBrandSeller,
): Promise<Map<string, SaleStat> | null> => {
  try {
    const { data } = await api_.get<GiftItemLite[]>(
      `/${role}/gift-item?select=${encodeURIComponent('giftItemInfo priceRetail')}`,
    );
    const map = new Map<string, SaleStat>();
    data.forEach((item) => {
      const infoId = infoIdOf(item);
      if (!infoId) return;
      const price = item.priceRetail ?? 0;
      const prev = map.get(infoId);
      if (!prev) {
        map.set(infoId, { count: 1, minPrice: price, maxPrice: price });
      } else {
        prev.count += 1;
        prev.minPrice = Math.min(prev.minPrice, price);
        prev.maxPrice = Math.max(prev.maxPrice, price);
      }
    });
    return map;
  } catch {
    //* 라우트 미개방(404) 등 — 이 정보 없이도 페이지는 정상 동작
    return null;
  }
};

const brandNameOf = (info: GiftItemInfoData): string => {
  const brand = info.giftItemBrand;
  return (typeof brand === 'object' ? brand?.name : undefined) || '기타';
};

const brandIdOf = (info: GiftItemInfoData): string | undefined => {
  const brand = info.giftItemBrand;
  return typeof brand === 'object' ? brand?._id : brand;
};

const imageListOf = (images: unknown): Image[] =>
  Array.isArray(images)
    ? images.filter(
        (img): img is Image =>
          Boolean(img) && typeof img === 'object' && 'url' in (img as object),
      )
    : [];

export const GiftBrandItemListPage: FC = function GiftBrandItemListPage() {
  const { giftBrandRole } = useAuth();
  const role = giftBrandRole() as LowerRole.Admin | LowerRole.GiftBrandSeller;

  useGiftItemInfoListHook(role);
  const infoList = useRecoilValue(giftItemInfoListDataAtom);

  const [{ value: saleStats }, loadSaleStats] = useAsyncFn(
    () => fetchSaleStats(role),
    [role],
  );
  useEffect(() => {
    loadSaleStats();
  }, [loadSaleStats]);

  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [searchText, setSearchText] = useState('');
  const [detailInfo, setDetailInfo] = useState<GiftItemInfoData | null>(null);

  //* 담당 브랜드가 여럿일 때만 브랜드 선택을 보여준다 (Admin은 전 브랜드)
  const brands = useMemo(() => {
    const map = new Map<string, string>();
    (infoList ?? []).forEach((info) => {
      const id = brandIdOf(info);
      if (id && !map.has(id)) map.set(id, brandNameOf(info));
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [infoList]);

  const items = useMemo(() => {
    const keywords = searchText.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (infoList ?? [])
      .filter((info) => !selectedBrandId || brandIdOf(info) === selectedBrandId)
      .filter((info) => {
        if (statusFilter === 'onSale') return info.isEnabled !== false;
        if (statusFilter === 'ended') return info.isEnabled === false;
        return true;
      })
      .filter((info) => {
        if (typeFilter === 'coupon') return info.type === GiftItemInfoType.Coupon;
        if (typeFilter === 'delivery') return info.type !== GiftItemInfoType.Coupon;
        return true;
      })
      .filter((info) => {
        if (keywords.length === 0) return true;
        const haystack = [info.name, ...(info.tags ?? []), info.subCategory]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return keywords.every((keyword) => haystack.includes(keyword));
      })
      .sort((a, b) => {
        //* 판매중 먼저, 그 다음 표시 순서
        const enabledA = a.isEnabled === false ? 1 : 0;
        const enabledB = b.isEnabled === false ? 1 : 0;
        if (enabledA !== enabledB) return enabledA - enabledB;
        return (
          (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
        );
      });
  }, [infoList, selectedBrandId, statusFilter, typeFilter, searchText]);

  const summary = useMemo(() => {
    const scoped = (infoList ?? []).filter(
      (info) => !selectedBrandId || brandIdOf(info) === selectedBrandId,
    );
    return {
      onSale: scoped.filter((info) => info.isEnabled !== false).length,
      ended: scoped.filter((info) => info.isEnabled === false).length,
    };
  }, [infoList, selectedBrandId]);

  if (!infoList) {
    return (
      <div className="center-box py-16">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-bold">판매상품관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          지금 판매 중인 상품과 판매중지 상품, 현재 가격을 확인하세요. 상품을 누르면
          고객에게 보이는 상세 내용을 볼 수 있습니다. (조회 전용)
        </p>
      </div>

      {/* 요약 */}
      <div className="mb-4 flex gap-2">
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
          <p className="text-xs text-gray-500">판매중</p>
          <p className="text-lg font-bold text-indigo-600">{summary.onSale}개</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-2">
          <p className="text-xs text-gray-500">판매중지</p>
          <p className="text-lg font-bold text-gray-500">{summary.ended}개</p>
        </div>
      </div>

      {/* 브랜드 선택 (담당 브랜드가 여럿일 때만) */}
      {brands.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <button
            onClick={() => setSelectedBrandId(null)}
            className={
              selectedBrandId === null
                ? 'rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white'
                : 'rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50'
            }
          >
            전체
          </button>
          {brands.map((brand) => (
            <button
              key={brand.id}
              onClick={() => setSelectedBrandId(brand.id)}
              className={
                brand.id === selectedBrandId
                  ? 'rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white'
                  : 'rounded-full border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50'
              }
            >
              {brand.name}
            </button>
          ))}
        </div>
      )}

      {/* 검색·필터 */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="상품명·태그 검색"
            className="rounded-md border border-gray-300 py-1.5 pl-8 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
        <div className="flex gap-1">
          {(
            [
              { key: 'all', label: '전체' },
              { key: 'onSale', label: '판매중' },
              { key: 'ended', label: '판매중지' },
            ] as { key: StatusFilter; label: string }[]
          ).map((chip) => (
            <button
              key={chip.key}
              onClick={() => setStatusFilter(chip.key)}
              className={
                statusFilter === chip.key
                  ? 'rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white'
                  : 'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50'
              }
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {(
            [
              { key: 'all', label: '전체' },
              { key: 'coupon', label: '쿠폰형' },
              { key: 'delivery', label: '배송형' },
            ] as { key: TypeFilter; label: string }[]
          ).map((chip) => (
            <button
              key={chip.key}
              onClick={() => setTypeFilter(chip.key)}
              className={
                typeFilter === chip.key
                  ? 'rounded-md bg-gray-700 px-3 py-1.5 text-xs font-medium text-white'
                  : 'rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50'
              }
            >
              {chip.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{items.length}개</span>
      </div>

      {/* 목록 */}
      {items.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          조건에 맞는 상품이 없습니다.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          {items.map((info) => {
            const isCoupon = info.type === GiftItemInfoType.Coupon;
            const ended = info.isEnabled === false;
            const discount = giftDiscountRate(
              info.priceRegular ?? 0,
              info.priceRetail ?? 0,
            );
            const thumbUrl = giftImageThumbUrl(info.image);
            const stat = saleStats?.get(info._id);

            return (
              <div
                key={info._id}
                onClick={() => setDetailInfo(info)}
                className={`flex cursor-pointer gap-3 rounded-lg border bg-white p-3 transition hover:border-indigo-300 hover:shadow-sm ${
                  ended ? 'border-gray-200 opacity-70' : 'border-gray-200'
                }`}
              >
                {thumbUrl ? (
                  <img
                    src={thumbUrl}
                    alt={info.name}
                    className="h-20 w-20 flex-shrink-0 rounded-md object-cover"
                  />
                ) : (
                  <div className="h-20 w-20 flex-shrink-0 rounded-md bg-gray-100" />
                )}

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-bold text-white ${
                        isCoupon ? 'bg-purple-500' : 'bg-blue-500'
                      }`}
                    >
                      {isCoupon ? '쿠폰' : '배송형'}
                    </span>
                    {info.isSelectable && (
                      <span className="rounded bg-indigo-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
                        선택형
                      </span>
                    )}
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${
                        ended
                          ? 'bg-gray-200 text-gray-600'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {ended ? '판매중지' : '판매중'}
                    </span>
                    {brands.length > 1 && (
                      <span className="text-[11px] text-gray-400">
                        {brandNameOf(info)}
                      </span>
                    )}
                  </div>

                  <p className="mt-1 break-keep text-sm font-bold leading-snug text-gray-900">
                    {info.name}
                  </p>

                  {(info.tags ?? []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(info.tags ?? []).map((tag, i) => (
                        <span
                          key={`${tag}-${i}`}
                          className="rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-medium text-teal-700"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 가격 */}
                  <div className="mt-1.5 flex flex-wrap items-baseline gap-x-4 gap-y-0.5 text-xs">
                    <span className="text-gray-400">
                      정가 {formatPrice(info.priceRegular ?? 0)}원
                    </span>
                    <span className="font-bold text-gray-900">
                      판매가 {formatPrice(info.priceRetail ?? 0)}원
                      {discount > 0 && (
                        <span className="ml-1 font-extrabold text-red-500">
                          {discount}%
                        </span>
                      )}
                    </span>
                    {info.priceWhole > 0 && (
                      <span className="text-gray-400">
                        공급가 {formatPrice(info.priceWhole)}원
                      </span>
                    )}
                    {info.minOrderQuantity > 1 && (
                      <span className="text-gray-400">
                        최소 {info.minOrderQuantity}개
                      </span>
                    )}
                  </div>

                  {/* 상조별 실제 판매 현황 (조회 가능할 때만) */}
                  {stat && (
                    <p className="mt-1 text-[11px] text-gray-400">
                      상조 {stat.count}곳에서 판매 중
                      {stat.minPrice !== info.priceRetail ||
                      stat.maxPrice !== info.priceRetail
                        ? ` · 실제 판매가 ${formatPrice(stat.minPrice)}${
                            stat.minPrice !== stat.maxPrice
                              ? `~${formatPrice(stat.maxPrice)}`
                              : ''
                          }원`
                        : ''}
                    </p>
                  )}
                  {saleStats && !stat && (
                    <p className="mt-1 text-[11px] text-amber-600">
                      상점에 등록된 상조가 없습니다
                    </p>
                  )}
                </div>

                <div className="flex flex-shrink-0 items-center">
                  <span className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-600">
                    상세보기
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 고객에게 보이는 상세 (전화 응대 화면과 같은 모달) */}
      {detailInfo && (
        <GiftItemPreviewModal
          onClose={() => setDetailInfo(null)}
          source={{
            name: detailInfo.name,
            image: detailInfo.image,
            type: detailInfo.type,
            isSelectable: detailInfo.isSelectable,
            isEnabled: detailInfo.isEnabled,
            priceRegular: detailInfo.priceRegular ?? 0,
            priceRetail: detailInfo.priceRetail ?? 0,
            detailInfo: detailInfo.detailInfo,
            tags: detailInfo.tags,
            minOrderQuantity: detailInfo.minOrderQuantity,
            orderUnit: detailInfo.orderUnit,
            couponDetail: detailInfo.couponDetail,
            deliveryDetail: detailInfo.deliveryDetail,
            allowIndividualDelivery: detailInfo.allowIndividualDelivery,
            useThanksPrint: detailInfo.useThanksPrint,
            additionalOptions: detailInfo.additionalOptions,
            choiceUnits: detailInfo.choiceUnits,
            detailImages: imageListOf(detailInfo.detailImages),
            extraRows: [
              ...(detailInfo.priceWhole > 0
                ? [{ label: '공급가', value: `${formatPrice(detailInfo.priceWhole)}원` }]
                : []),
              {
                label: '판매 상태',
                value: detailInfo.isEnabled === false ? '판매종료' : '판매중',
              },
            ],
          }}
        />
      )}
    </div>
  );
};
